import { ref, computed, inject, watch } from 'vue'
import { getAvailableFields } from '@/configurations/financials/tamaro'
import { loadScript, centsToAmount } from '@/utils/utils'
import { get as _get, set as _set, cloneDeep as _cloneDeep } from 'lodash'

const tamaroSymbol = Symbol()

export const createTamaro = {
  install: app => {
    const version = process.env.VUE_APP_TAMARO_VERSION || 'latest'
    const tamaro_fields = getAvailableFields()

    const disposers = ref([])
    const config = ref()
    const payment_form = ref()
    const custom_keys = ref([])
    const required_fields = ref([])

    const is_tamaro_loaded = ref(false)
    const tamaro_validation_errors = ref({})

    const requiredTamaroFields = computed(() => Object.entries(tamaro_fields).reduce((acc, [key, field]) => required_fields.value.includes(key) ? [...acc, field] : acc, []))
    const requiredTamaroModels = computed(() => requiredTamaroFields.value.map(field => field.model))

    const loadTamaroWidget = async (configuration = {}) => {
      if (window.rnw?.tamaro) return

      try {
        config.value = configuration

        await loadScript(`https://tamaro.raisenow.com/koalect/${version}/widget.js`)

        setupTamaroEvents()

        await window.rnw.tamaro.createWidget(config.value)

        is_tamaro_loaded.value = true
      } catch (error) {
        throw error
      }
    }

    const destroyTamaroWidget = () => {
      disposers.value.forEach(dispose => dispose?.())
      disposers.value = []

      window.rnw?.tamaro?.destroy()

      payment_form.value = {}
      is_tamaro_loaded.value = false
    }

    const renderTamaroWidget = async id => {
      try {
        await window.rnw.tamaro.renderWidget(window.rnw.tamaro.instance, id)
      } catch (error) {
        throw error
      }
    }

    const validateTamaroWidget = async () => window.rnw?.tamaro?.instance?.paymentForm.validateBeforeSend()

    const resetTamaroWiget = () => window.rnw?.tamaro?.instance?.retryPayment()

    const sendToTamaro = async () => window.rnw?.tamaro?.instance?.paymentForm.send()

    const setupTamaroEvents = () => {
      disposers.value.push(
        window.rnw?.tamaro?.events.afterCreate.subscribe(async event => {
          const tamaro = event.data.api
          const { reaction, comparer, toJS } = await window.rnw.tamaro.libs?.mobx()

          disposers.value.push(
            reaction(
              () => toJS(tamaro.paymentForm.allValidationErrors),
              errors => tamaro_validation_errors.value = errors,
              { fireImmediately: true, equals: comparer.structural }
            )
          )

          disposers.value.push(
            reaction(
              () => toJS(tamaro.paymentForm.requiredFields),
              fields => required_fields.value = fields,
              { fireImmediately: true, equals: comparer.structural }
            )
          )

          disposers.value.push(
            watch(
              config,
              () => tamaro.extendConfig(_cloneDeep(config.value)),
              { deep: true, immediate: false }
            )
          )
        })
      )
    }

    const getTamaroPaymentMethod = () => window.rnw?.tamaro?.instance?.paymentForm.getPaymentMethod()

    const setTamaroPaymentMethods = payment_methods => _set(config.value, 'paymentMethods', payment_methods)

    const getTamaroPaymentType = () => window.rnw?.tamaro?.instance?.paymentForm.getPaymentType()

    const setTamaroAmount = amount => window.rnw?.tamaro?.instance?.paymentForm.setAmount(centsToAmount(amount))

    const setTamaroAmounts = amounts => config.value = _set(config.value, 'amounts', amounts)

    const setTamaroPaymentFormData = (data = {}) => {
      if (!window.rnw?.tamaro?.instance?.paymentForm) return

      cleanTamaroPaymentFormData(data)

      Object.keys(data).forEach(key => {
        _set(window.rnw.tamaro.instance.paymentForm, `data.${key}`, data[key])

        if (!custom_keys.value.includes(key)) custom_keys.value.push(key)
      })
    }

    const setTamaroPaymentFormStaticData = (data = {}) => {
      if (!window.rnw?.tamaro?.instance?.paymentForm) return

      Object.keys(data).forEach(key => _set(window.rnw.tamaro.instance.paymentForm, `data.${key}`, data[key]))
    }

    const cleanTamaroPaymentFormData = (data = {}) => {
      if (!window.rnw?.tamaro?.instance?.paymentForm) return

      custom_keys.value.forEach(key => {
        if (!(key in data)) {
          delete window.rnw.tamaro.instance.paymentForm.data[key]

          custom_keys.value = custom_keys.value.filter(k => k !== key)
        }
      })
    }

    const afterTamaroWidgetRender = callback => {
      if (callback) disposers.value.push(window.rnw?.tamaro.events.afterRender.subscribe(() => callback()))
    }

    const beforeTamaroPaymentValidateAndSend = callback => {
      if (callback) disposers.value.push(window.rnw?.tamaro.events.beforePaymentValidateAndSend.subscribe(() => callback()))
    }

    const tamaroPaymentMethodChanged = callback => {
      if (callback) {
        callback(getTamaroPaymentMethod())

        disposers.value.push(window.rnw?.tamaro.events.paymentMethodChanged.subscribe(() => callback(getTamaroPaymentMethod())))
      }
    }

    const tamaroPaymentComplete = callback => {
      disposers.value.push(
        window.rnw?.tamaro.events.fetchPaymentDataEnd.subscribe(e => {
          const key = getTamaroPaymentType() === 'recurring' ? 'epmsSubscriptionInfo' : 'epmsPaymentInfo'
          const transaction_id = _get(e, `data.${key}.custom_parameters.koa_transaction_id`)
          const status = getTamaroPaymentType() === 'recurring' ? _get(e, `data.${key}.status`) : _get(e, `data.${key}.last_status`)

          if (!status || !transaction_id) return
          if (callback) callback({ status, transaction_id })

          resetTamaroWiget()
        })
      )
    }

    const tamaroSendPaymentError = callback => {
      disposers.value.push(
        window.rnw?.tamaro.events.sendEpmsPaymentError.subscribe(() => {
          if (callback) callback()

          resetTamaroWiget()
        })
      )
    }

    app.provide(tamaroSymbol, {
      is_tamaro_loaded,
      tamaro_fields,
      tamaro_validation_errors,
      requiredTamaroFields,
      requiredTamaroModels,
      loadTamaroWidget,
      destroyTamaroWidget,
      renderTamaroWidget,
      validateTamaroWidget,
      sendToTamaro,
      setTamaroAmount,
      setTamaroAmounts,
      setTamaroPaymentMethods,
      setTamaroPaymentFormData,
      setTamaroPaymentFormStaticData,
      afterTamaroWidgetRender,
      beforeTamaroPaymentValidateAndSend,
      tamaroPaymentMethodChanged,
      tamaroPaymentComplete,
      tamaroSendPaymentError
    })
  }
}

export const useTamaro = () => inject(tamaroSymbol)

